// @ts-nocheck
import escapeHtml from 'escape-html'
import { getEmbeddedComponentType, serializeEmbeddedElement, safeArticleUrl, safeArticleLink } from './embeddedComponents'
import { Text } from 'slate'
import { jsx } from 'slate-hyperscript'
import { CustomElementType, DATA_NO_AIME, H_TAGS } from './interface'
import { readCellSpans, serializeCellSpans } from './utils/tableConversion'
const TAGNAMECONTAINER = ['a', 'p', 'li', 'ul', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'div', 'td', 'th'] // 当前支持可以包裹的span标签的标签

// Give inline cell content a single paragraph before table normalization runs;
// otherwise the plugin wraps each text/mark/link in a separate paragraph.
const getCellChildren = (children) => {
  const result = []
  let inlineChildren = []
  const flush = () => {
    if (inlineChildren.length) {
      result.push(jsx('element', { type: CustomElementType.paragraph }, inlineChildren))
      inlineChildren = []
    }
  }
  children.forEach((child) => {
    if (isInlineNode(child)) {
      inlineChildren.push(child)
    } else {
      flush()
      result.push(child)
    }
  })
  flush()
  return result
}

const isInlineNode = (node) =>
  !node?.type ||
  ['a', 'stock', 'span'].includes(node.type) ||
  (node.type === CustomElementType.embeddedComponent && node.componentType === 'data-vis')
const isBlockNode = (node) => node && !isInlineNode(node)

const splitAroundBlockChildren = (children, inlineAttributes) => {
  const result = []
  let inlineChildren = []
  const flushInlineChildren = () => {
    if (inlineChildren.length) result.push(jsx('element', inlineAttributes, inlineChildren))
    inlineChildren = []
  }
  for (const child of children) {
    if (isBlockNode(child)) {
      flushInlineChildren()
      result.push(child)
    } else if (child) {
      inlineChildren.push(child)
    }
  }
  flushInlineChildren()
  return result
}

const normalizeMixedBlockChildren = (children) => (children.some(isBlockNode) ? splitAroundBlockChildren(children, { type: 'paragraph' }) : children)

const isWhitespaceTextNode = (node) => node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()
const flattenParsedValue = (value) => (Array.isArray(value) ? value.flat(Infinity) : [value])
const hasRenderableValue = (value) => flattenParsedValue(value).some(Boolean)
const containsBlockNode = (value) => flattenParsedValue(value).some(isBlockNode)
const isIgnorableAdjacentEntry = (entry) => isWhitespaceTextNode(entry.source) || !hasRenderableValue(entry.value)

const shouldRemoveFormattingWhitespace = (entries, index, containerName) => {
  if (!isWhitespaceTextNode(entries[index].source)) return false
  if (['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR'].includes(containerName)) {
    return /^[\t\n\r\f ]*$/.test(entries[index].source.textContent || '')
  }
  if (['OL', 'UL'].includes(containerName)) return true
  if (!['BODY', 'BLOCKQUOTE', 'LI', ...H_TAGS.map((tag) => tag.toUpperCase())].includes(containerName)) return false

  let previousIndex = index - 1
  while (previousIndex >= 0 && isIgnorableAdjacentEntry(entries[previousIndex])) previousIndex -= 1
  let nextIndex = index + 1
  while (nextIndex < entries.length && isIgnorableAdjacentEntry(entries[nextIndex])) nextIndex += 1

  const previousIsBlock = previousIndex >= 0 && containsBlockNode(entries[previousIndex].value)
  const nextIsBlock = nextIndex < entries.length && containsBlockNode(entries[nextIndex].value)
  return (previousIndex < 0 && nextIsBlock) || (nextIndex >= entries.length && previousIsBlock) || (previousIsBlock && nextIsBlock)
}

export const serializeHTML = (node: CustomNode) => {
  const style = node?.align ? ` style="text-align: ${node.align}"` : ''
  if (Text.isText(node)) {
    let string = escapeHtml(node.text)
    if (node.softBreak) string = string.replace(/\r\n|\r|\n/g, '<br>')
    if (node.code) string = `<code>${string}</code>`
    if (node.bold) {
      string = `<strong>${string}</strong>`
    }
    if (node.italic) {
      string = `<em>${string}</em>`
    }
    if (node.underline) {
      string = `<u>${string}</u>`
    }
    if (node.fontSize) {
      string = `<span style="font-size: ${node.fontSize}">${string}</span>`
    }

    if (node.commentHighlight && node.commentId) {
      string = `<span id="${node.commentId}" class="comment-highlight">${string}</span>`
    }
    return string
  }

  const children = node.children?.map((n) => serializeHTML(n)).join('')

  switch (node.type) {
    case CustomElementType.embeddedComponent:
      return node.componentHtml || ''
    case 'numbered-list':
      return `<ol${style}${node.start ? ` start="${escapeHtml(String(node.start))}"` : ''}>${children}</ol>`
    case 'blockquote':
      return `<blockquote>${children}</blockquote>`
    case 'pre':
      return `<pre>${children}</pre>`
    case 'hr':
      return '<hr>'
    case CustomElementType.backtest:
      const backtestAttributes = ['code', 'id', 'market', 'range', 'timerangeid']
        .filter((name) => typeof node.backtestAttributes?.[name] === 'string')
        .map((name) => ` ${name}="${escapeHtml(node.backtestAttributes[name])}"`)
        .join('')
      return `<backtest-component${backtestAttributes}></backtest-component>`
    case 'image':
      const stringifyNode = escapeHtml(JSON.stringify(node))
      if (node.href) {
        return `<a href="${escapeHtml(node.href)}" target='__blank'><img data-json="${stringifyNode}" src="${escapeHtml(node.src)}" />${children}</a>`
      }
      return `<img data-json="${stringifyNode}" src="${escapeHtml(
        node.src,
      )}" contentEditable="false" style="display: block; margin: 0 auto; maxWidth: 100%" />${children}`
    case 'bulleted-list':
      return `<ul${style}>${children}</ul>`
    case 'list-item':
      return `<li${style}>${children}</li>`
    case 'a':
      const stockAttributes = node.marketId ? ` data-marketid="${escapeHtml(node.marketId)}" data-code="${escapeHtml(node.code || '')}"` : ''
      return `<a href="${escapeHtml(safeArticleLink(node.href || ''))}" target='__blank'${stockAttributes}>${children}</a>`
    case 'stock':
      return `<a href="${escapeHtml(node.href)}" target='__blank' data-marketid="${node.marketId}" data-code="${node.code}">${children}</a>`
    case 'paragraph':
      // 对表格单元格里面的<p>做处理,加上自定义属性:data-no-anime,避免aime那边插入问句
      if (node[DATA_NO_AIME]) {
        return `<p${style} ${DATA_NO_AIME}='true'>${children}</p>`
      }

      return `<p${style}>${children}</p>`
    // 可视化图表存储
    case 'visualization':
      return `<visualization data='${escapeHtml(node.data || '')}' dataurl='${escapeHtml(node.dataurl || '')}'>${children}</visualization>`
    case CustomElementType.htmlVisualization:
      return `<${CustomElementType.htmlVisualization} data-html="${escapeHtml(node.html || '')}">${children}</${CustomElementType.htmlVisualization}>`
    case 'text2img':
      return `<text2img data='${escapeHtml(JSON.stringify(node.data))}'>${children}</text2img>`
    case 'text2visual':
      return `<text2visual data='${escapeHtml(JSON.stringify(node.data))}'>${children}</text2visual>`
    case CustomElementType.richTable:
      // 保存表格上的自定义数据
      const tableCustomData = escapeHtml(JSON.stringify(node.data))
      // 给表格增加默认边框
      return `<${CustomElementType.richTable} border='1' data="${tableCustomData}" >${children}</${CustomElementType.richTable}>`
    case CustomElementType.richTableHead:
      const tableBodyCustomData = escapeHtml(JSON.stringify(node.data))
      return `<${CustomElementType.richTableHead} data="${tableBodyCustomData}" >${children}</${CustomElementType.richTableHead}>`
    case CustomElementType.richTableBody:
      const tableHeadCustomData = escapeHtml(JSON.stringify(node.data))
      return `<${CustomElementType.richTableBody} data="${tableHeadCustomData}" >${children}</${CustomElementType.richTableBody}>`
    case CustomElementType.richTableFoot:
      const tableFootCustomData = escapeHtml(JSON.stringify(node.data))
      return `<${CustomElementType.richTableFoot} data="${tableFootCustomData}">${children}</${CustomElementType.richTableFoot}>`
    case CustomElementType.richTableRow:
      const tableRowCustomData = escapeHtml(JSON.stringify(node.data))
      return `<${CustomElementType.richTableRow} data="${tableRowCustomData}">${children}</${CustomElementType.richTableRow}>`
    case CustomElementType.richTableCell:
      const tableCellCustomData = escapeHtml(JSON.stringify(node.data))
      return `<${CustomElementType.richTableCell} colspan="${Number(node.colSpan) || 1}" rowspan="${
        Number(node.rowSpan) || 1
      }" data="${tableCellCustomData}">${children}</${CustomElementType.richTableCell}>`
    case CustomElementType.richTableHeadCell:
      const tableHeadCellCustomData = escapeHtml(JSON.stringify(node.data))
      return `<${CustomElementType.richTableHeadCell} colspan="${Number(node.colSpan) || 1}" rowspan="${
        Number(node.rowSpan) || 1
      }" data="${tableHeadCellCustomData}">${children}</${CustomElementType.richTableHeadCell}>`
    case CustomElementType.video:
      const videoData = escapeHtml(JSON.stringify(node.data))
      return `<video src="${escapeHtml(safeArticleUrl(node.data.videoUrl || ''))}"  poster="${escapeHtml(
        safeArticleUrl(node.data.coverUrl || ''),
      )}" data="${videoData}">${children}</video>`
    case CustomElementType.cardInfos:
      return node.componentHtml || `<${CustomElementType.cardInfos}>${children}</${CustomElementType.cardInfos}>`
  }

  if (H_TAGS.includes(node.type)) {
    return `<${node.type}${style}>${children}</${node.type}>`
  }

  return children
}

export const deserialize = (el, markAttributes = {}) => {
  if (el.nodeType === Node.TEXT_NODE) {
    return jsx('text', markAttributes, el.textContent)
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return null
  }

  const embeddedType = getEmbeddedComponentType(el)
  if (embeddedType) {
    return jsx(
      'element',
      {
        type: CustomElementType.embeddedComponent,
        componentType: embeddedType,
        componentHtml: serializeEmbeddedElement(el),
        componentText: el.textContent || '',
      },
      [{ text: '' }],
    )
  }

  const nodeAttributes = { ...markAttributes }

  switch (el.nodeName) {
    case 'CODE':
      nodeAttributes.code = true
      break
    case 'STRONG':
      nodeAttributes.bold = true
      break
    case 'B':
      if (el.style.fontWeight !== 'normal') {
        nodeAttributes.bold = true
      }
      break
    case 'EM':
    case 'I':
      nodeAttributes.italic = true
      break
    case 'U':
      nodeAttributes.underline = true
      break
    case 'SPAN':
      if (el.style.fontSize) {
        nodeAttributes.fontSize = el.style.fontSize
      }

      // 处理font-weight
      if (el.style.fontWeight === '700' || el.style.fontWeight === 'bold') {
        nodeAttributes.bold = true
      }

      // 处理font-style
      if (el.style.fontStyle === 'italic') {
        nodeAttributes.italic = true
      }

      // 处理text-decoration
      if (el.style.textDecoration === 'underline') {
        nodeAttributes.underline = true
      }

      if (el.classList.contains('comment-highlight')) {
        nodeAttributes.commentHighlight = true
        nodeAttributes.commentId = el.id
      }

      break
  }

  const rawChildren = Array.from(el.childNodes)
  const childEntries = rawChildren.map((source) => ({
    source,
    value: deserialize(source, nodeAttributes),
  }))
  const children = childEntries
    // Only block-to-block whitespace is source formatting. Whitespace between
    // inline content remains meaningful, including around a lifted card.
    .filter((_, index) => !shouldRemoveFormattingWhitespace(childEntries, index, el.nodeName))
    .map(({ value }) => value)
    .flat()
    .filter((node) => node != null)
  if (children.length === 0) {
    children.push(jsx('text', nodeAttributes, ''))
  }

  switch (el.nodeName) {
    case 'META':
      // meta标签不需要转换
      return null
    case 'HEAD':
      // head标签不需要转换
      return null
    case 'BODY':
      // Slate requires top-level blocks, including for a standalone inline visualization.
      const blocks = []
      let inlineNodes = []
      const flushInlineNodes = () => {
        if (inlineNodes.length) blocks.push(jsx('element', { type: 'paragraph' }, inlineNodes))
        inlineNodes = []
      }
      for (const child of children) {
        if (!child) continue
        if (
          !child.type ||
          ['a', 'stock'].includes(child.type) ||
          (child.type === CustomElementType.embeddedComponent && child.componentType === 'data-vis')
        ) {
          inlineNodes.push(child)
        } else {
          flushInlineNodes()
          blocks.push(child)
        }
      }
      flushInlineNodes()
      return jsx('fragment', {}, blocks)
    case 'BR':
      return jsx('text', { ...nodeAttributes, softBreak: true }, '\n')
    case 'OL':
      return jsx('element', { type: 'numbered-list', start: el.getAttribute('start'), align: el.style.textAlign }, children)
    case 'BLOCKQUOTE':
      return jsx('element', { type: 'blockquote' }, normalizeMixedBlockChildren(children))
    case 'PRE':
      return jsx('element', { type: 'pre' }, [{ text: el.textContent || '', code: true }])
    case 'HR':
      return jsx('element', { type: 'hr' }, [{ text: '' }])
    case 'P':
      const attributes = {
        type: 'paragraph',
        align: el.style.textAlign ?? '',
      }
      // 对表格单元格里面的<p>做处理,加上自定义属性:data-no-anime,避免aime那边插入问
      const noAime = el.hasAttribute(DATA_NO_AIME) && el.getAttribute(DATA_NO_AIME)
      if (noAime) {
        attributes[DATA_NO_AIME] = true
      }

      // Split parsed blocks, including blocks nested in spans, before Slate can discard them.
      if (children.some(isBlockNode)) return splitAroundBlockChildren(children, attributes)
      return jsx('element', attributes, children)
    case 'A':
      if (el.getAttribute('data-marketid')) {
        // 股票代码
        return jsx(
          'element',
          {
            type: 'a',
            href: el.getAttribute('href'),
            marketId: el.getAttribute('data-marketid'),
            code: el.getAttribute('data-code'),
          },
          children,
        )
      }
      return jsx(
        // 纯链接
        'element',
        { type: 'a', href: el.getAttribute('href') },
        children,
      )
    case 'IMG':
      let commonAttribute = {}
      if (el.getAttribute('data-json')) {
        commonAttribute = JSON.parse(el.getAttribute('data-json'))
      }

      return jsx(
        'element',
        {
          ...commonAttribute,
          type: 'image',
          src: el.getAttribute('src'),
        },
        children,
      )
    case 'UL':
      return jsx('element', { type: 'bulleted-list', align: el.style.textAlign ?? '' }, children)
    case 'LI':
      return jsx(
        'element',
        {
          type: 'list-item',
          align: el.style.textAlign ?? '',
        },
        normalizeMixedBlockChildren(children),
      )
    case 'SPAN':
      // A span is always inline. Block containers such as BODY are
      // responsible for wrapping top-level inline content in paragraphs.
      return children
    case CustomElementType.backtest.toUpperCase():
      const backtestAttributes = {}
      for (const name of ['code', 'id', 'market', 'range', 'timerangeid']) {
        const value = el.getAttribute(name)
        if (value !== null) backtestAttributes[name] = value
      }
      return jsx(
        'element',
        {
          type: CustomElementType.backtest,
          backtestAttributes,
        },
        [{ text: '' }],
      )
    case 'VISUALIZATION':
      const data = el.getAttribute('data')
      const dataurl = el.getAttribute('dataurl')
      return jsx('element', { type: CustomElementType.visualization, data, dataurl }, children)
    case CustomElementType.htmlVisualization.toUpperCase():
      return jsx('element', { type: CustomElementType.htmlVisualization, html: el.getAttribute('data-html') || '' }, children)
    case 'TEXT2IMG':
      // 检查 text2img 标签内部是否有 img 标签（可能是之前替换失败或外部导入的脏数据）
      const imgChild = Array.from(el.childNodes).find((node: any) => node.nodeName === 'IMG')
      if (imgChild) {
        // 如果内部有 img 标签，直接返回 Image 节点，而不是 text2img 节点
        const imgElement = imgChild as Element
        let commonAttribute = {}
        if (imgElement.getAttribute('data-json')) {
          commonAttribute = JSON.parse(imgElement.getAttribute('data-json'))
        }
        return jsx(
          'element',
          {
            ...commonAttribute,
            type: 'image',
            src: imgElement.getAttribute('src'),
          },
          [{ text: '' }],
        )
      }
      const text2imgData = getStringifyData(el.getAttribute('data'))
      return jsx('element', { type: CustomElementType.text2img, data: text2imgData }, children)
    case 'TEXT2VISUAL':
      const text2visualData = getStringifyData(el.getAttribute('data'))
      return jsx('element', { type: CustomElementType.text2visual, data: text2visualData }, children)
    /**
     * DOMParser解析嵌套的自定义标签会出问题,所以先把<rich-table>替换成<table>
     * 此处暂时用TABLE来匹配
     */
    case 'TABLE':
      const tableCustomData = getStringifyData(el.getAttribute('data')) || {}
      return jsx('element', { type: CustomElementType.richTable, data: tableCustomData }, children)
    case CustomElementType.richTableHead.toUpperCase():
      const tableHeadCustomData = getStringifyData(el.getAttribute('data')) || {}
      return jsx('element', { type: CustomElementType.richTableHead, data: tableHeadCustomData }, children)
    case CustomElementType.richTableBody.toUpperCase():
      const tableBodyCustomData = getStringifyData(el.getAttribute('data')) || {}
      return jsx('element', { type: CustomElementType.richTableBody, data: tableBodyCustomData }, children)
    case CustomElementType.richTableFoot.toUpperCase():
      const tableFootCustomData = getStringifyData(el.getAttribute('data')) || {}
      return jsx('element', { type: CustomElementType.richTableFoot, data: tableFootCustomData }, children)
    case CustomElementType.richTableRow.toUpperCase():
      const tableRowCustomData = getStringifyData(el.getAttribute('data')) || {}
      return jsx('element', { type: CustomElementType.richTableRow, data: tableRowCustomData }, children)
    case CustomElementType.richTableCell.toUpperCase():
      const tableCellCustomData = getStringifyData(el.getAttribute('data')) || {}
      return jsx(
        'element',
        {
          type: CustomElementType.richTableCell,
          data: tableCellCustomData,
          ...readCellSpans(el),
        },
        getCellChildren(children),
      )
    case CustomElementType.richTableHeadCell.toUpperCase():
      const tableHeadCellCustomData = getStringifyData(el.getAttribute('data')) || {}
      return jsx(
        'element',
        {
          type: CustomElementType.richTableHeadCell,
          data: tableHeadCellCustomData,
          ...readCellSpans(el),
        },
        getCellChildren(children),
      )
    case CustomElementType.video.toUpperCase():
      const videoData = getStringifyData(el.getAttribute('data')) || {}
      videoData.videoUrl = safeArticleUrl(el.getAttribute('src') || el.querySelector('source')?.getAttribute('src') || videoData.videoUrl || '')
      videoData.coverUrl = safeArticleUrl(el.getAttribute('poster') || videoData.coverUrl || '')
      return jsx('element', { type: CustomElementType.video, data: videoData }, [{ text: '' }])
    case CustomElementType.cardInfos.toUpperCase():
      return jsx(
        'element',
        {
          type: CustomElementType.cardInfos,
          componentHtml: serializeEmbeddedElement(el),
        },
        [{ text: '' }],
      )
  }

  if (H_TAGS.includes(el.nodeName.toLowerCase())) {
    const attributes = { type: el.nodeName.toLowerCase(), align: el.style.textAlign ?? '' }
    if (children.some(isBlockNode)) return splitAroundBlockChildren(children, attributes)
    return jsx('element', attributes, children)
  }

  return children
}

/**
 * 获取编辑器的html
 * @param editor 要获取html的编辑器
 * @returns
 */
export const getEditorHtml = (editor) => {
  const { children } = editor
  let html = ''
  children.forEach((child) => {
    html += serializeHTML(child)
  })
  return html
}

// 获取JSON序列化格式的字符串解析后的数据
const getStringifyData = (str: string) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    return undefined
  }
}
